import type { LayoutConfig } from '@/utils/dealCalculations'
import { findCategoryCoords, groupServicesByCategory, type PlanServiceItem } from '@/utils/dealCalculations'
import { formatCategoryDisplayTitle, formatServiceLineLabel, normalizeTextEncoding } from '@/utils/text-normalization'
import type PptxGenJS from 'pptxgenjs'

export type CategoryBlockPercent = {
  categoria: string
  items: PlanServiceItem[]
  x: number
  y: number
  widthPercent: number
}

const GREEN_ACCENT = '40C351'

export type PptxCategoryTypography = {
  serviceFontSize: number
  categoryFontSize: number
  spacingAfterCategory: number
  lineSpacingMultiplier: number
  itemGap: number
  layoutLineHeight: number
}

/** Tipografia por densidade de itens no slide (evita sobreposição no Site Institucional). */
export function resolvePptxCategoryTypography(totalItems: number): PptxCategoryTypography {
  if (totalItems > 28) {
    return {
      serviceFontSize: 6.5,
      categoryFontSize: 8,
      spacingAfterCategory: 0.16,
      lineSpacingMultiplier: 0.11,
      itemGap: 0.008,
      layoutLineHeight: 2,
    }
  }
  if (totalItems > 18) {
    return {
      serviceFontSize: 7,
      categoryFontSize: 9,
      spacingAfterCategory: 0.2,
      lineSpacingMultiplier: 0.13,
      itemGap: 0.01,
      layoutLineHeight: 4.2,
    }
  }
  if (totalItems > 12) {
    return {
      serviceFontSize: 8,
      categoryFontSize: 10,
      spacingAfterCategory: 0.24,
      lineSpacingMultiplier: 0.15,
      itemGap: 0.015,
      layoutLineHeight: 2.8,
    }
  }
  return {
    serviceFontSize: 9,
    categoryFontSize: 11,
    spacingAfterCategory: 0.32,
    lineSpacingMultiplier: 0.18,
    itemGap: 0.03,
    layoutLineHeight: 3.1,
  }
}

function layoutLeftBlockWidthPercent(cfg: LayoutConfig, override?: number): number {
  if (override !== undefined) return override > 1 ? override : override * 100
  const raw = cfg.leftBlockWidth ?? 35
  return raw > 1 ? raw : raw * 100
}

/**
 * Posicionamento dos blocos de categoria (%). Usado no preview do Studio e no export PPTX.
 */
export function computeCategoryBlocksPercent(
  layout: LayoutConfig,
  serviceGroups: Map<string, PlanServiceItem[]>,
  leftBlockWidth?: number,
): CategoryBlockPercent[] {
  const entries = Array.from(serviceGroups.entries())
  const totalItems = entries.reduce((sum, [, items]) => sum + items.length, 0)
  const typography = resolvePptxCategoryTypography(totalItems)
  const leftBlockWidthVal = layoutLeftBlockWidthPercent(layout, leftBlockWidth)
  const col1X = layout.serviceBlockX || 38
  const col2X = layout.serviceBlockGap || 68
  const startY = layout.serviceBlockStartY || 10
  let leftY = startY
  let rightY = startY
  const placed: Array<{ x: number; y: number; h: number }> = []

  const overlaps = (x: number, y: number, h: number) =>
    placed.some((p) => Math.abs(p.x - x) < 6 && y < p.y + p.h - 0.3 && y + h > p.y + 0.3)

  return entries.map(([categoria, items], idx) => {
    const isLeft = idx % 2 === 0
    const savedCoords = findCategoryCoords(layout.categoryCoords, categoria)
    const lineHeight = typography.layoutLineHeight
    const blockHeight = 4 + items.length * lineHeight

    const autoX = isLeft ? col1X : col2X
    const autoY = isLeft ? leftY : rightY
    let x = autoX
    let y = autoY

    if (savedCoords) {
      x = savedCoords.x
      y = savedCoords.y
    } else if (!overlaps(autoX, autoY, blockHeight)) {
      x = autoX
      y = autoY
    }

    placed.push({ x, y, h: blockHeight })

    if (!savedCoords) {
      if (isLeft) leftY += blockHeight + 1.5
      else rightY += blockHeight + 1.5
    } else {
      if (x <= col1X + 2) leftY = Math.max(leftY, y + blockHeight + 1.5)
      else rightY = Math.max(rightY, y + blockHeight + 1.5)
    }

    const midpoint = (leftBlockWidthVal + 100) / 2
    const widthPercent = x < midpoint ? col2X - col1X - 2 : 100 - col2X - 4

    return {
      categoria: formatCategoryDisplayTitle(categoria),
      items: items.map((item) => ({
        ...item,
        nome: normalizeTextEncoding(item.nome),
        quantity: item.quantity ? normalizeTextEncoding(String(item.quantity)) : item.quantity,
      })),
      x,
      y,
      widthPercent,
    }
  })
}

export function buildPptxCategoryBlocks(
  layout: LayoutConfig,
  servicos: PlanServiceItem[],
  slideW: number,
  slideH: number,
  leftBlockWidth?: number,
): Array<CategoryBlockPercent & { xInches: number; yInches: number; wInches: number }> {
  const groups = groupServicesByCategory(servicos)
  return computeCategoryBlocksPercent(layout, groups, leftBlockWidth).map((block) => ({
    ...block,
    xInches: (block.x / 100) * slideW,
    yInches: (block.y / 100) * slideH,
    wInches: Math.max(1.2, (block.widthPercent / 100) * slideW),
  }))
}

export function renderPptxCategoryBlocks(
  slide: PptxGenJS.Slide,
  blocks: Array<{ categoria: string; items: PlanServiceItem[]; xInches: number; yInches: number; wInches: number }>,
  options: {
    totalItems: number
    categoryColor?: string
    itemColor?: string
    cardFill?: string
    cardLine?: string
  },
): void {
  const {
    serviceFontSize,
    categoryFontSize,
    spacingAfterCategory,
    lineSpacingMultiplier,
    itemGap,
  } = resolvePptxCategoryTypography(options.totalItems)

  for (const block of blocks) {
    if (options.cardFill) {
      slide.addShape('roundRect', {
        x: block.xInches - 0.08,
        y: block.yInches - 0.08,
        w: block.wInches + 0.16,
        h: Math.min(2.15, 0.62 + block.items.length * 0.25),
        fill: { color: options.cardFill, transparency: 10 },
        line: { color: options.cardLine || '2A2E45', transparency: 15, width: 1 },
        rectRadius: 0.08,
      })
    }
    slide.addText(block.categoria, {
      x: block.xInches,
      y: block.yInches,
      w: block.wInches,
      h: 0.35,
      fontSize: categoryFontSize,
      color: options.categoryColor || '111111',
      bold: true,
      fontFace: 'Arial',
      lang: 'pt-BR',
    })

    let currentItemY = block.yInches + spacingAfterCategory
    for (const item of block.items) {
      const itemNome = formatServiceLineLabel(item.nome || '', item.quantity)
      const lines = itemNome.length > 42 ? Math.ceil(itemNome.length / 42) : 1
      const itemH = Math.max(0.24, lines * lineSpacingMultiplier)
      const checkColor = item.isPartial ? 'D97706' : GREEN_ACCENT
      const checkChar = item.isPartial ? '◑  ' : '✓  '

      slide.addText(
        [
          { text: checkChar, options: { color: checkColor, fontSize: serviceFontSize, bold: true, lang: 'pt-BR' } },
          { text: itemNome, options: { color: options.itemColor || '454545', fontSize: serviceFontSize, lang: 'pt-BR' } },
        ],
        {
          x: block.xInches + 0.15,
          y: currentItemY,
          w: block.wInches - 0.15,
          h: itemH,
          fontFace: 'Arial',
          lang: 'pt-BR',
        },
      )

      currentItemY += itemH + itemGap
    }
  }
}
